/**
 * Compra, vendi o scambia su un exchange crypto (SPEC-024 §9).
 *
 * Tre modalita' in un modal solo, perche' sono tre modi di rispondere alla
 * stessa domanda — «cosa faccio con questo asset» — e chi la apre da una
 * posizione non deve sapere in anticipo quale sceglierà:
 *
 *  - **Compra / Vendi**: un ordine su una coppia `ASSET-VALUTA DI CONTO`,
 *    per importo o per quantita' (o per frazione del saldo, in vendita);
 *  - **Scambia**: da una crypto a un'altra. L'instradamento lo decide il
 *    backend — coppia diretta o due gambe attraverso la valuta di conto — e
 *    l'anteprima lo mostra, perche' un passaggio dalla cassa e' un'altra cosa
 *    rispetto a uno scambio secco, e va visto prima;
 *  - **Avanzato**: limite, stop e stop-limite, con validita' e scadenza.
 *
 * Stessa disciplina del modal Trading 212: **anteprima obbligatoria** calcolata
 * dal server con le stesse regole dell'invio, e nessuna autorizzazione
 * implicita — i limiti si consumano solo all'invio. Un exchange non ha demo:
 * l'avviso di denaro reale c'e' sempre.
 */

import { useMemo, useState } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDateTime } from '@/lib/format';
import { Alert, Badge, Button, Field, Modal, Select } from '@/ui';
import {
  useCryptoBalances,
  useCryptoCatalog,
  useExecuteSwap,
  usePlaceCryptoOrder,
  usePreviewCryptoOrder,
  usePreviewSwap,
  type CryptoOrderInput,
  type CryptoOrderPreview,
  type SwapPreview,
  type SwapResult,
} from './api';
import './manual-order.css';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/** Il codice resta accanto alla frase: due pubblici, due bisogni. */
const BLOCKER_LABEL: Record<string, string> = {
  connection_not_active: 'la connessione non è attiva: verificare la chiave',
  trading_disabled: 'il trading è disabilitato su questa connessione',
  live_not_allowed: 'il trading su denaro reale non è abilitato sul server (CONNECTORS_ALLOW_LIVE_TRADING)',
  no_price: "l'exchange non ha un prezzo per questa coppia",
  quantity_too_small: 'la quantità è troppo piccola dopo il troncamento agli incrementi della coppia',
  amount_too_small: "l'importo è troppo piccolo dopo il troncamento agli incrementi della coppia",
  amount_below_minimum: "l'importo è sotto il minimo che l'exchange accetta per questa coppia",
  amount_above_maximum: "l'importo supera il massimo che l'exchange accetta per questa coppia",
  quantity_above_maximum: "la quantità supera il massimo che l'exchange accetta per questa coppia",
  insufficient_balance: 'il saldo disponibile non basta',
  no_position: 'non risulta alcun saldo da vendere per questo asset',
  limit_exceeded: 'supererebbe i limiti operativi che hai configurato',
  size_ambiguous: 'indica una sola dimensione: quantità, importo o percentuale',
  order_type_unsupported: 'questo exchange non supporta questo tipo di ordine',
  time_in_force_unsupported: 'questo exchange non supporta questa validità',
  limit_price_required: 'serve un prezzo limite',
  trigger_price_required: 'serve un prezzo di attivazione',
  end_time_required: 'un ordine con scadenza richiede la data di scadenza',
  no_route: "l'exchange non offre alcuna via per questo scambio, nemmeno passando dalla valuta di conto",
  same_asset: 'i due asset devono essere diversi',
  proceeds_unknown: 'il ricavato della prima gamba non è stimabile',
};

const WARNING_LABEL: Record<string, string> = {
  fee_known_at_execution: "la commissione esatta la dice la quotazione al momento dell'esecuzione: qui non è stimabile",
  residual_quantity: "l'exchange non accetta abbastanza decimali per vendere tutto: resterà una frazione",
  two_leg_swap:
    'non esiste una coppia diretta: lo scambio passa dalla valuta di conto in due operazioni, e la seconda parte solo quando la prima è eseguita',
};

function describeBlocker(code: string): string {
  // I blocker delle gambe di uno scambio arrivano come `COPPIA:codice`.
  const [head, tail] = code.includes(':') ? code.split(/:(.+)/) : [null, code];
  const label = BLOCKER_LABEL[tail ?? code] ?? tail ?? code;
  return head ? `${head}: ${label}` : label;
}

type Tab = 'trade' | 'swap' | 'advanced';
type SizeMode = 'amount' | 'quantity' | 'percent';
const SELL_PERCENT_PRESETS = [25, 50, 75, 100] as const;
type SellPercent = (typeof SELL_PERCENT_PRESETS)[number];

export type CryptoTradeModalProps = {
  connectionId: string;
  /** Valuta di conto del portafoglio (`EUR`): la quote delle coppie di Compra/Vendi. */
  accountCurrency: string;
  /** Aperto da una posizione: l'asset e' gia' scelto e resta visibile ma bloccato. */
  fixedAsset?: string;
  defaultSide?: 'buy' | 'sell';
  onClose: () => void;
};

export function CryptoTradeModal({ connectionId, accountCurrency, fixedAsset, defaultSide, onClose }: CryptoTradeModalProps) {
  const catalog = useCryptoCatalog(connectionId);
  const balances = useCryptoBalances(connectionId);
  const previewOrder = usePreviewCryptoOrder();
  const placeOrder = usePlaceCryptoOrder();
  const previewSwap = usePreviewSwap();
  const executeSwap = useExecuteSwap();

  const [tab, setTab] = useState<Tab>('trade');
  const [asset, setAsset] = useState(fixedAsset ?? '');
  const [side, setSide] = useState<'buy' | 'sell'>(defaultSide ?? 'buy');
  const [mode, setMode] = useState<SizeMode>('amount');
  const [value, setValue] = useState('');
  const [percent, setPercent] = useState<SellPercent>(100);
  const [toAsset, setToAsset] = useState('');
  // Avanzato
  const [orderType, setOrderType] = useState<'limit' | 'stop' | 'stop_limit'>('limit');
  const [limitPrice, setLimitPrice] = useState('');
  const [triggerPrice, setTriggerPrice] = useState('');
  const [timeInForce, setTimeInForce] = useState<'gtc' | 'gtd' | 'ioc' | 'fok'>('gtc');
  const [endTime, setEndTime] = useState('');

  const [orderPreview, setOrderPreview] = useState<CryptoOrderPreview | null>(null);
  const [swapPreview, setSwapPreview] = useState<SwapPreview | null>(null);
  const [swapResult, setSwapResult] = useState<SwapResult | null>(null);

  const quote = accountCurrency.toUpperCase();
  const capabilities = catalog.data?.capabilities ?? null;
  // Un exchange a quotazioni non ha ordini limite ne' stop: la scheda «Avanzato» non ha senso.
  const advancedAvailable = capabilities ? capabilities.orderTypes.includes('limit') : true;

  /** Le crypto acquistabili contro la valuta di conto: quelle con una coppia `X-QUOTE`. */
  const tradableAssets = useMemo(() => {
    const pairs = catalog.data?.pairs ?? [];
    const names = new Map(catalog.data?.assets.map((a) => [a.symbol, a.name]) ?? []);
    return pairs
      .filter((pair) => pair.quoteAsset === quote && pair.baseType === 'crypto')
      .map((pair) => ({ symbol: pair.baseAsset, name: names.get(pair.baseAsset) ?? pair.baseAsset }))
      .sort((a, b) => a.symbol.localeCompare(b.symbol));
  }, [catalog.data, quote]);

  /** Gli asset che si possiedono (saldo > 0): il punto di partenza di uno scambio o di una vendita. */
  const heldAssets = useMemo(
    () =>
      (balances.data?.items ?? [])
        .filter((b) => b.asset !== quote && Number(b.available) > 0)
        .map((b) => b.asset)
        .sort(),
    [balances.data, quote],
  );

  const availableOf = (symbol: string) => balances.data?.items.find((b) => b.asset === symbol)?.available ?? null;

  // Ogni modifica invalida l'anteprima: numeri vecchi accanto a un pulsante
  // «Invia» sono il modo peggiore di sbagliare su un percorso che muove denaro.
  const invalidate = <T,>(setter: (next: T) => void) => (next: T) => {
    setOrderPreview(null);
    setSwapPreview(null);
    setter(next);
  };

  const setSideSafely = (next: 'buy' | 'sell') => {
    setOrderPreview(null);
    setSide(next);
    if (next === 'buy' && mode === 'percent') setMode('amount');
  };

  const pair = asset ? `${asset}-${quote}` : '';

  const buildOrderInput = (): CryptoOrderInput | null => {
    if (!asset) return null;
    const size =
      mode === 'percent' ? { sellPercent: percent } : mode === 'amount' ? { amount: value.trim() } : { quantity: value.trim() };
    if (mode !== 'percent' && value.trim() === '') return null;
    if (tab === 'advanced') {
      return {
        pair,
        side,
        orderType,
        ...size,
        ...(orderType !== 'stop' ? { limitPrice: limitPrice.trim() } : {}),
        ...(orderType !== 'limit' ? { triggerPrice: triggerPrice.trim() } : {}),
        timeInForce,
        ...(timeInForce === 'gtd' && endTime ? { endTime: new Date(endTime).toISOString() } : {}),
      };
    }
    return { pair, side, orderType: 'market', ...size };
  };

  const runOrderPreview = () => {
    const input = buildOrderInput();
    if (!input) return;
    previewOrder.mutate({ connectionId, ...input }, { onSuccess: setOrderPreview });
  };

  const submitOrder = () => {
    const input = buildOrderInput();
    if (!input || !orderPreview?.canSubmit) return;
    placeOrder.mutate({ connectionId, ...input }, { onSuccess: onClose });
  };

  const buildSwapInput = () => {
    if (!asset || !toAsset || asset === toAsset) return null;
    if (mode === 'percent') return { fromAsset: asset, toAsset, percent };
    if (value.trim() === '') return null;
    return { fromAsset: asset, toAsset, quantity: value.trim() };
  };

  const runSwapPreview = () => {
    const input = buildSwapInput();
    if (!input) return;
    previewSwap.mutate({ connectionId, ...input }, { onSuccess: setSwapPreview });
  };

  const submitSwap = () => {
    const input = buildSwapInput();
    if (!input || !swapPreview?.canSubmit) return;
    executeSwap.mutate(
      { connectionId, ...input },
      {
        onSuccess: (result) => {
          // Uno scambio `partial` NON chiude il modal: la prima gamba e' partita
          // e la seconda no, e la persona deve leggerlo qui, adesso.
          if (result.status === 'completed') onClose();
          else setSwapResult(result);
        },
      },
    );
  };

  const isSwapTab = tab === 'swap';
  const sizeOptions = [
    { value: 'amount', label: isSwapTab ? `Per quantità di ${asset || 'asset'}` : `Per importo in ${quote}` },
    ...(isSwapTab ? [] : [{ value: 'quantity', label: 'Per quantità' }]),
    ...(side === 'sell' || isSwapTab ? [{ value: 'percent', label: 'Per percentuale del saldo' }] : []),
  ];
  // Nello scambio la dimensione e' SEMPRE una quantita' dell'asset ceduto: il
  // primo modo si chiama «amount» solo per riusare lo stato, ma manda `quantity`.
  const effectiveMode: SizeMode = isSwapTab && mode === 'quantity' ? 'amount' : mode;

  return (
    <Modal title="Compra, vendi o scambia" onClose={onClose}>
      <div className="manual-order">
        <div className="manual-order__presets" role="tablist" aria-label="Tipo di operazione">
          {(
            [
              ['trade', 'Compra / Vendi'],
              ['swap', 'Scambia'],
              ...(advancedAvailable ? ([['advanced', 'Avanzato']] as const) : []),
            ] as const
          ).map(([key, label]) => (
            <Button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              variant={tab === key ? 'primary' : 'secondary'}
              onClick={() => {
                invalidate(setTab)(key);
                setSwapResult(null);
                if (key === 'swap' && mode === 'quantity') setMode('amount');
              }}
            >
              {label}
            </Button>
          ))}
        </div>

        <Alert variant="warning" title="Denaro reale">
          Un exchange non ha un ambiente di prova: ogni ordine inviato da qui opera sul conto vero e non è
          annullabile una volta eseguito.
          {capabilities?.tradingModel === 'quotes' &&
            ' Questo conto negozia a quotazione immediata: l’invio chiede un prezzo e lo accetta nello stesso gesto.'}
        </Alert>

        {catalog.isError && (
          <Alert variant="error" title="Catalogo non disponibile">
            {errorMessage(catalog.error, "Impossibile leggere le coppie dell'exchange")}
          </Alert>
        )}

        <Select
          label={isSwapTab ? 'Asset da cedere' : 'Asset'}
          value={asset}
          onChange={(event) => invalidate(setAsset)(event.target.value)}
          options={(isSwapTab || side === 'sell' ? heldAssets.map((s) => ({ symbol: s, name: s })) : tradableAssets).map((item) => ({
            value: item.symbol,
            label: item.name === item.symbol ? item.symbol : `${item.symbol} — ${item.name}`,
          }))}
          placeholder={catalog.isLoading ? 'Caricamento del catalogo…' : 'Scegli un asset…'}
          disabled={Boolean(fixedAsset)}
          hint={
            fixedAsset
              ? "Aperto dalla posizione: l'asset è già scelto."
              : isSwapTab || side === 'sell'
                ? 'Solo gli asset di cui hai un saldo disponibile.'
                : `Le crypto acquistabili contro ${quote} su questo exchange.`
          }
          required
        />

        {asset && availableOf(asset) && (
          <div className="manual-order__row manual-order__row--muted">
            <span>saldo disponibile</span>
            <span>
              {availableOf(asset)} {asset}
            </span>
          </div>
        )}

        {isSwapTab ? (
          <Select
            label="Asset da ricevere"
            value={toAsset}
            onChange={(event) => invalidate(setToAsset)(event.target.value)}
            options={tradableAssets
              .filter((item) => item.symbol !== asset)
              .map((item) => ({ value: item.symbol, label: `${item.symbol} — ${item.name}` }))}
            placeholder="Scegli un asset…"
            hint="Se non esiste una coppia diretta, lo scambio passa dalla valuta di conto in due operazioni: l'anteprima lo dice."
            required
          />
        ) : (
          <Select
            label="Operazione"
            value={side}
            onChange={(event) => setSideSafely(event.target.value as 'buy' | 'sell')}
            options={[
              { value: 'buy', label: 'Acquisto' },
              { value: 'sell', label: 'Vendita' },
            ]}
          />
        )}

        {tab === 'advanced' && (
          <>
            <Select
              label="Tipo di ordine"
              value={orderType}
              onChange={(event) => invalidate(setOrderType)(event.target.value as 'limit' | 'stop' | 'stop_limit')}
              options={[
                { value: 'limit', label: 'Limite' },
                { value: 'stop', label: 'Stop (a mercato al prezzo di attivazione)' },
                { value: 'stop_limit', label: 'Stop-limite' },
              ]}
            />
            {orderType !== 'stop' && (
              <Field label={`Prezzo limite (${quote})`} value={limitPrice} onChange={(e) => invalidate(setLimitPrice)(e.target.value)} numeric />
            )}
            {orderType !== 'limit' && (
              <Field
                label={`Prezzo di attivazione (${quote})`}
                value={triggerPrice}
                onChange={(e) => invalidate(setTriggerPrice)(e.target.value)}
                numeric
              />
            )}
            <Select
              label="Validità"
              value={timeInForce}
              onChange={(event) => invalidate(setTimeInForce)(event.target.value as 'gtc' | 'gtd' | 'ioc' | 'fok')}
              options={[
                { value: 'gtc', label: 'Fino a revoca (GTC)' },
                { value: 'gtd', label: 'Fino a una data (GTD)' },
                { value: 'ioc', label: 'Esegui subito o annulla il resto (IOC)' },
                { value: 'fok', label: 'Tutto subito o niente (FOK)' },
              ]}
            />
            {timeInForce === 'gtd' && (
              <Field label="Scadenza" type="datetime-local" value={endTime} onChange={(e) => invalidate(setEndTime)(e.target.value)} required />
            )}
          </>
        )}

        <Select
          label="Come indichi la dimensione"
          value={effectiveMode}
          onChange={(event) => invalidate(setMode)(event.target.value as SizeMode)}
          options={sizeOptions}
          hint={
            effectiveMode === 'percent'
              ? "La quantità è la frazione del saldo disponibile SULL'EXCHANGE adesso, troncata agli incrementi della coppia. Il 100% è «vendi tutto»."
              : effectiveMode === 'amount' && !isSwapTab
                ? `Spendi (o incassi) un importo in ${quote}: la quantità la calcola l'exchange al prezzo di esecuzione.`
                : "La quantità viene troncata verso il basso all'incremento che l'exchange accetta."
          }
        />

        {effectiveMode === 'percent' ? (
          <div className="manual-order__presets" role="group" aria-label="Percentuale del saldo">
            {SELL_PERCENT_PRESETS.map((preset) => (
              <Button
                key={preset}
                type="button"
                variant={percent === preset ? 'primary' : 'secondary'}
                aria-pressed={percent === preset}
                onClick={() => invalidate(setPercent)(preset)}
              >
                {preset}%
              </Button>
            ))}
          </div>
        ) : (
          <Field
            label={effectiveMode === 'amount' && !isSwapTab ? `Importo (${quote})` : `Quantità (${asset || 'asset'})`}
            value={value}
            onChange={(event) => invalidate(setValue)(event.target.value)}
            numeric
          />
        )}

        <div className="manual-order__actions">
          <Button
            type="button"
            variant="secondary"
            onClick={isSwapTab ? runSwapPreview : runOrderPreview}
            disabled={
              previewOrder.isPending ||
              previewSwap.isPending ||
              !asset ||
              (isSwapTab && !toAsset) ||
              (effectiveMode !== 'percent' && value.trim() === '')
            }
          >
            {previewOrder.isPending || previewSwap.isPending ? 'Calcolo…' : 'Calcola anteprima'}
          </Button>
        </div>

        {(previewOrder.error || previewSwap.error) && (
          <Alert variant="error" title="Anteprima non riuscita">
            {errorMessage(previewOrder.error ?? previewSwap.error, 'Errore imprevisto')}
          </Alert>
        )}

        {orderPreview && !isSwapTab && <OrderPreviewPanel preview={orderPreview} />}
        {swapPreview && isSwapTab && <SwapPreviewPanel preview={swapPreview} />}

        {swapResult && (
          <Alert variant={swapResult.status === 'partial' ? 'warning' : 'error'} title="Scambio eseguito solo in parte">
            {swapResult.message}
          </Alert>
        )}

        {(placeOrder.error || executeSwap.error) && (
          <Alert variant="error" title="Operazione non inviata">
            {errorMessage(placeOrder.error ?? executeSwap.error, 'Errore imprevisto')}
          </Alert>
        )}

        <div className="manual-order__actions">
          <Button type="button" variant="ghost" onClick={onClose} disabled={placeOrder.isPending || executeSwap.isPending}>
            {swapResult ? 'Chiudi' : 'Annulla'}
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={isSwapTab ? submitSwap : submitOrder}
            disabled={
              swapResult !== null ||
              placeOrder.isPending ||
              executeSwap.isPending ||
              (isSwapTab ? !swapPreview?.canSubmit : !orderPreview?.canSubmit)
            }
          >
            {placeOrder.isPending || executeSwap.isPending ? 'Invio…' : isSwapTab ? 'Esegui scambio reale' : 'Invia ordine reale'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Row({ label, value, muted = false }: { label: string; value: string | null | undefined; muted?: boolean }) {
  return (
    <div className={`manual-order__row${muted ? ' manual-order__row--muted' : ''}`}>
      <span>{label}</span>
      {muted ? <span>{value ?? '—'}</span> : <strong>{value ?? '—'}</strong>}
    </div>
  );
}

function Issues({ blockers, warnings }: { blockers: string[]; warnings: string[] }) {
  return (
    <>
      {warnings.map((code) => (
        <Alert key={code} variant="warning" title="Da tenere presente">
          {WARNING_LABEL[code] ?? code}
        </Alert>
      ))}
      {blockers.length > 0 && (
        <Alert variant="error" title="L'operazione non può partire">
          <ul>
            {blockers.map((code) => (
              <li key={code}>
                {describeBlocker(code)} <code>{code}</code>
              </li>
            ))}
          </ul>
        </Alert>
      )}
    </>
  );
}

function OrderPreviewPanel({ preview }: { preview: CryptoOrderPreview }) {
  return (
    <div className="manual-order__preview">
      <Row label="Coppia" value={preview.pair} />
      {preview.isSwap && (
        <Alert variant="info" title="È uno scambio">
          La quote di questa coppia è una crypto: non passa dalla cassa in {preview.accountCurrency}.
        </Alert>
      )}
      <Row label="Prezzo di riferimento (exchange)" value={preview.referencePrice ? `${preview.referencePrice} ${preview.quoteAsset}` : null} />
      {preview.referencePriceAt && <Row label="rilevato" value={formatDateTime(preview.referencePriceAt)} muted />}
      {preview.heldQuantity && <Row label="Saldo disponibile" value={`${preview.heldQuantity} ${preview.baseAsset}`} />}
      <Row
        label={preview.sizedBy === 'amount' ? 'Importo che verrà inviato' : 'Quantità che verrà inviata'}
        value={preview.sizedBy === 'amount' ? (preview.amount ? `${preview.amount} ${preview.quoteAsset}` : null) : preview.quantity ? `${preview.quantity} ${preview.baseAsset}` : null}
      />
      <Row
        label={preview.sizedBy === 'amount' ? 'Quantità stimata' : 'Controvalore stimato'}
        value={preview.sizedBy === 'amount' ? (preview.quantity ? `${preview.quantity} ${preview.baseAsset}` : null) : preview.amount ? `${preview.amount} ${preview.quoteAsset}` : null}
        muted
      />
      {preview.residualQuantity && <Row label="resterà sul conto" value={`${preview.residualQuantity} ${preview.baseAsset}`} muted />}
      {preview.estimatedAccountValue && preview.quoteAsset !== preview.accountCurrency && (
        <Row label={`Controvalore in ${preview.accountCurrency}`} value={preview.estimatedAccountValue} />
      )}
      <Row
        label="Commissione stimata"
        value={preview.estimatedFee ? `${preview.estimatedFee} ${preview.quoteAsset}${preview.feePct ? ` (${preview.feePct}%)` : ''}` : null}
        muted
      />
      {preview.constraints.minOrderAmount && (
        <Row label="minimo per ordine" value={`${preview.constraints.minOrderAmount} ${preview.quoteAsset}`} muted />
      )}
      <Row label="Limiti operativi" value={preview.limitsSummary} muted />
      <div className="manual-order__row">
        <span>Ambiente</span>
        <Badge tone="negative">denaro reale</Badge>
      </div>
      <Issues blockers={preview.blockers} warnings={preview.warnings} />
    </div>
  );
}

function SwapPreviewPanel({ preview }: { preview: SwapPreview }) {
  return (
    <div className="manual-order__preview">
      <Row
        label="Instradamento"
        value={
          preview.route === 'direct'
            ? 'coppia diretta, una sola operazione'
            : preview.route === 'via_quote'
              ? `due operazioni attraverso ${preview.via}`
              : 'nessuna via disponibile'
        }
      />
      <Row label={`Cedi`} value={preview.fromQuantity ? `${preview.fromQuantity} ${preview.fromAsset}` : null} />
      {preview.availableFrom && <Row label="saldo disponibile" value={`${preview.availableFrom} ${preview.fromAsset}`} muted />}
      {preview.residualQuantity && <Row label="resterà sul conto" value={`${preview.residualQuantity} ${preview.fromAsset}`} muted />}
      <Row label="Ricevi (stima, al netto delle commissioni)" value={preview.estimatedToQuantity ? `≈ ${preview.estimatedToQuantity} ${preview.toAsset}` : null} />
      {preview.estimatedAccountValue && <Row label={`Controvalore in ${preview.accountCurrency}`} value={preview.estimatedAccountValue} muted />}
      {preview.legs.map((leg, index) => (
        <Row
          key={`${leg.pair}-${index}`}
          label={`${index + 1}. ${leg.side === 'buy' ? 'acquisto' : 'vendita'} ${leg.pair}`}
          value={`${leg.sizedBy === 'amount' ? `importo ${leg.amount ?? '—'}` : `quantità ${leg.quantity ?? '—'}`}${leg.referencePrice ? ` @ ${leg.referencePrice}` : ''}${leg.estimatedFee ? ` · fee ≈ ${leg.estimatedFee}` : ''}`}
          muted
        />
      ))}
      <Row label="Limiti operativi" value={preview.limitsSummary} muted />
      <Issues blockers={preview.blockers} warnings={preview.warnings} />
    </div>
  );
}
